import {
    CmsHeadingElementType,
    CmsHeadingStyle,
} from '@activebrands/core-web/libs/storyblok/content/cms-module-content';
import { type RawSubHeadingData, type StoryblokEditable } from '@activebrands/core-web/libs/storyblok/storyblok';
import transformPreviewEditable from '@activebrands/core-web/libs/storyblok/utils/transform-preview-editable';

const SUB_HEADING_FALLBACK_STYLE = 'md';
const SUB_HEADING_FALLBACK_TYPE = 'h2';

const allowedTypes: { [key: string]: string } = { h1: 'h1', h2: 'h2', h3: 'h3', h4: 'h4', h5: 'h5', h6: 'h6' };
const allowedStyles: { [key: string]: string } = { sm: 'sm', md: 'md', lg: 'lg' };

export type SubHeadingProp = RawSubHeadingData | RawSubHeadingData[] | undefined;

type TransformSubHeadingProps = {
    defaultStyle?: CmsHeadingStyle;
    defaultType?: CmsHeadingElementType;
    subHeading: SubHeadingProp;
};

export type SubHeadingType = {
    component?: 'subHeading';
    id?: string;
    sbEditable?: StoryblokEditable;
    style: string;
    text: string;
    type: string;
};

/**
 * Transforms raw sub heading data from Storyblok into a standardized sub heading object
 *
 * @param props - The transformation parameters
 * @param props.defaultStyle - Default style to use if no style is provided
 * @param props.defaultType - Default heading type to use if no type is provided
 * @param props.subHeading - The raw sub heading data to transform
 * @param props.subHeading._editable - The editable of the sub heading
 * @param props.subHeading._uid - The uid of the sub heading
 * @param props.subHeading.component - The component of the sub heading
 * @param props.subHeading.style - The style of the sub heading
 * @param props.subHeading.text - The text of the sub heading
 * @param props.subHeading.type - The type of the sub heading
 * @returns Transformed sub heading object or undefined if no valid sub heading data
 *
 */
const transformSubHeading = ({
    defaultStyle,
    defaultType,
    subHeading,
}: TransformSubHeadingProps): SubHeadingType | undefined => {
    if (!subHeading || (Array.isArray(subHeading) && !subHeading?.[0])) {
        return;
    }

    const subHeadingData = Array.isArray(subHeading) ? subHeading[0] : subHeading;
    const { _uid, _editable, component, style, text, type } = subHeadingData;

    const customStyle = style && style !== 'default' && allowedStyles[style];
    const fallbackStyle = (defaultStyle && allowedStyles[defaultStyle]) || SUB_HEADING_FALLBACK_STYLE;
    const customType = type && type !== 'default' && allowedTypes[type];
    const fallbackType = (defaultType && allowedTypes[defaultType]) || SUB_HEADING_FALLBACK_TYPE;

    return {
        component,
        id: _uid,
        sbEditable: transformPreviewEditable(_editable),
        style: customStyle || fallbackStyle,
        text,
        type: customType || fallbackType,
    };
};

export default transformSubHeading;
